import React, { useState, useEffect, useRef } from "react";
import jwtDecode from "jwt-decode";
import { useIntl, FormattedMessage } from "react-intl";
import { useSnackbar } from "notistack";
import {
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Button,
  makeStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  createMuiTheme,
  FormControlLabel,
  Slide,
  IconButton,
} from "@material-ui/core";
import { Info, Timer } from "@material-ui/icons";
import { useDispatch } from "react-redux";
import { ThemeProvider } from "@material-ui/styles";
import { Switch, Tooltip } from "@material-ui/core";

// CUSTOM IMPORTS
import { LayoutSplashScreen } from "../../../_metronic/layout";
import {
  buildAsanAuthorizationUrl,
  ERROR_CODES,
  TIMER_CONSTANTS,
} from "./authConstants";
import Registration from "./Registration";
import { LanguageSelectorDropdown } from "../../components/Language/LanguageSelectorDropdown";
import { setVoen } from "../../../redux/features/login/loginSlice";
import { fetchToken } from "../../../redux/features/login/loginActions";
import { fetchNotifications } from "../../../redux/features/notifications/notificationActions";
import * as asanService from "../../../services/Asan";
import { InstitutionVoens } from "./constants";
import { objects, AsanSignResponseCodes } from "../../utils/constants";
import {
  getDigitalLoginCertificates,
  getDigitalLoginSessions,
  getDigitalLoginToken,
  getObject,
} from "../../../services/Login";
import { getUrlParamByName } from "app/utils/utilityFunctions";

// ASSETS
import bg from "../../images/bg-logo.jpg";
import digitalLoginLogo from "../../images/Digital-Login.jpg";
import mygovidlogo from "../../images/mygov-id.svg";
import { getTaxesObject, getTaxesPayer } from "services/taxes.service";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  gridHV: {
    height: "80vh",
  },
  cardW: {
    width: "828px",
    margin: "auto",
  },
  cardBody: {
    display: "flex",
    height: "400px",
    paddingTop: "40px",
    justifyContent: "space-between",
    alignItems: "center",
  },
  langBtn: {
    backgroundColor: "transparent !important",
    borderColor: "white !important",
  },
  btnContinue: {
    backgroundColor: "#543195",
    textTransform: "capitalize",
    width: "160px",
    color: "#fff",
    height: "45px",
    marginLeft: "10px",
    "&:hover": {
      backgroundColor: "#8a74b5",
    },
  },
  btnContinueDisable: {
    backgroundColor: "rgba(0, 0, 0, 0.12)",
    textTransform: "capitalize",
    width: "160px",
    color: "#fff !important",
    height: "45px",
    marginLeft: "10px",
  },
}));

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const Login = ({ history }) => {
  const classesMaterial = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const dispatch = useDispatch();
  const voenInputLabel = useRef(null);
  const pinInputLabel = useRef(null);
  const registeredStakeholdersInputLabel = useRef(null);
  const timerIntervalRef = useRef(null);

  const [voenLabelWidth, setVoenLabelWidth] = useState(0);
  const [pinLabelWidth, setPinLabelWidth] = useState(0);
  const [
    registeredStakeholdersLabelWidth,
    setRegisteredStakeholdersLabelWidth,
  ] = useState(0);
  const [registrationView, setRegistrationView] = useState(false);
  const [submitBtnDisabled, setSubmitBtnDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [voens, setVoens] = useState([]);
  const [personDetails, setPersonDetails] = useState(null);
  const [selectedVoen, setSelectedVoen] = useState("");
  const [selectedStakeholderId, setSelectedStakeholderId] = useState("");
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [voenIsRequired, setVoenIsRequired] = useState(false);
  const [registeredStakeholders, setRegisteredStakeholders] = useState([]);
  const [open, setOpen] = useState(false);
  const [confirmCheck, setConfirmCheck] = useState(false);
  const [agreement, setAgreement] = useState(true);
  const [pin2, setPin2] = useState("");
  const [asanModalState, setAsanModalState] = useState(false);
  const [asanSignVerified, setAsanSignVerified] = useState("");
  const [structureName, setStructureName] = useState(null);
  const [structureVoen, setStructureVoen] = useState("");
  const [seconds, setSeconds] = useState(TIMER_CONSTANTS.INITIAL_SECONDS);
  const [showLoginButton, setShowLoginButton] = useState(null); // null = checking, true = show, false = hide

  // const { currentStakeholder } = useSelector((state) => state.stakeholder);

  useEffect(() => {
    // Check if user is returning from Digital Login with code/state params
    // OR check if tokens exist in localStorage (page reload case)
    getTokensAndCertsByCodeAndState();
  }, []);

  useEffect(() => {
    setVoenLabelWidth(voenInputLabel?.current?.offsetWidth);
    setPinLabelWidth(pinInputLabel?.current?.offsetWidth);
    setRegisteredStakeholdersLabelWidth(
      registeredStakeholdersInputLabel?.current?.offsetWidth
    );
  });

  // Effect to handle timer reaching zero
  useEffect(() => {
    if (seconds === 0) {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      setSeconds(TIMER_CONSTANTS.INITIAL_SECONDS);
      setAsanModalState(false);
    }
  }, [seconds]);

  // Cleanup interval only on component unmount
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, []);

  const handleClickOpen = (e) => {
    setOpen(true);
    // setSubmitBtnDisabled(state);
    setConfirmCheck(e.target.checked);
  };

  const handleClose = (state) => {
    if (state === true) {
      setAgreement(false);
    } else {
      setConfirmCheck(false);
      setAgreement(true);
    }
    setOpen(false);
    // setSubmitBtnDisabled(state);
  };

  const handleAsanLogin = () => {
    const authorizationAsanUrl = buildAsanAuthorizationUrl();
    window.location.href = authorizationAsanUrl;
  };

  const checkJwtTokenExpire = (token) => {
    let isExpired = false;

    if (!token) {
      return true;
    }
    let decodedToken = jwtDecode(token);

    if (decodedToken) {
      const currentTime = Date.now() / 1000;

      if (decodedToken?.exp < currentTime) {
        isExpired = true;
      }
    } else {
      isExpired = true;
    }

    return isExpired;
  };

  async function readTokensFromLocalAndSet() {
    console.log("[LOGIN DEBUG] readTokensFromLocalAndSet: Starting...");
    try {
      const localDLToken = sessionStorage.getItem("asanLoginToken");
      const localDLIdToken = sessionStorage.getItem("asanLoginIdToken");
      console.log(
        "[LOGIN DEBUG] readTokensFromLocalAndSet: Tokens from localStorage",
        {
          hasToken: !!localDLToken,
          hasIdToken: !!localDLIdToken,
        }
      );

      if (localDLToken) {
        const isIdTokenExpire = checkJwtTokenExpire(localDLIdToken);
        const isDLTokenExpire = checkJwtTokenExpire(localDLToken);

        console.log(
          "[LOGIN DEBUG] readTokensFromLocalAndSet: Token expiration check",
          {
            isIdTokenExpire,
            isDLTokenExpire,
          }
        );

        if (isIdTokenExpire) {
          console.log(
            "[LOGIN DEBUG] readTokensFromLocalAndSet: Token expired, clearing storage and showing login button"
          );
          // sessionStorage.removeItem("asanLoginToken");
          // sessionStorage.removeItem("asanLoginIdToken");
          // localStorage.removeItem("lastDLCode");
          // localStorage.removeItem("lastDLState");
          // sessionStorage.removeItem("token");
          // localStorage.removeItem("currentStakeholder");
          sessionStorage.clear();
          localStorage.clear();

          // const authorizationAsanUrl = `${
          //     process.env.REACT_APP_DIGITAL_LOGIN_URL
          // }/grant-permission?client_id=${
          //     process.env.REACT_APP_DIGITAL_LOGIN_CLIENT_ID
          // }&redirect_uri=${
          //     process.env.REACT_APP_CABINET_URL
          // }&response_type=code&state=${uuidv4()}&scope=openid certificate session`;
          // console.log(authorizationAsanUrl);
          // window.location.href = authorizationAsanUrl;

          setShowLoginButton(true);
          setLoading(false);
          return;
        }

        console.log(
          "[LOGIN DEBUG] readTokensFromLocalAndSet: Fetching certificates..."
        );
        const certs = await getDigitalLoginCertificates(localDLToken);
        console.log(
          "[LOGIN DEBUG] readTokensFromLocalAndSet: Certificates fetched",
          {
            hasCerts: !!certs,
            certificateCount:
              certs?.message?.availableCertificates?.length ||
              certs?.message?.certificates?.length ||
              0,
          }
        );
        getDigitalLoginSessions(localDLToken).then((response) => {
          console.log(
            "[LOGIN DEBUG] readTokensFromLocalAndSet: Sessions fetched",
            {
              loginType: response?.message?.login_type,
            }
          );
          if (response?.message?.login_type) {
            sessionStorage.setItem("login-type", response?.message?.login_type);
          }
        });
        let decodedIdToken = jwtDecode(localDLIdToken);

        let person = {};

        if (decodedIdToken?.user) {
          person = JSON.parse(JSON.stringify(decodedIdToken.user));
        }

        if (certs?.message?.loginDetail) {
          person.phoneNumber = certs?.message?.loginDetail?.phoneNumber;
          person.userId = certs?.message?.loginDetail?.userId;
        }

        const availableVoens = [];

        const certificateList =
          certs?.message?.availableCertificates ||
          certs?.message?.certificates ||
          [];
        const certificates = certificateList.filter((x) => x?.structureData);
        console.log(
          "[LOGIN DEBUG] readTokensFromLocalAndSet: Processing certificates",
          {
            certificateCount: certificates.length,
          }
        );

        if (certificates?.length) {
          for (let i = 0; i < certificates.length; ++i) {
            if (certificates[i]?.structureData) {
              const structureData = {
                certificateNumber: certificates[i]?.serialNumber,
                structureName: certificates[i]?.structureData?.structureName,
                voen: certificates[i]?.structureData?.voen,
              };
              availableVoens.push(structureData);
            }
          }
          console.log(
            "[LOGIN DEBUG] readTokensFromLocalAndSet: Available VOENs",
            {
              voenCount: availableVoens.length,
              voens: availableVoens.map((v) => ({
                voen: v.voen,
                name: v.structureName,
              })),
            }
          );
          setVoens(availableVoens);

          const firstCertificateItem = certificates[0];
          if (firstCertificateItem) {
            setStructureName(
              firstCertificateItem?.structureData?.structureName
            );
            setStructureVoen(firstCertificateItem?.structureData?.voen);
          }
          // if (!checkIsInstitution(person.structureData?.voen)) {
          //   setVoens(availableVoens);
          // }
        } else {
          setRegistrationView(true);
        }

        let voenIsRequired = false;

        if (availableVoens.length) {
          voenIsRequired = true;
          setSubmitBtnDisabled(true);
        }

        setPersonDetails((prevState) => ({
          ...prevState,
          ...person,
        }));
        setVoenIsRequired(voenIsRequired);
        setShowLoginButton(false); // Hide login button, show form
        setLoading(false);
        // Cleanup: OAuth code already processed successfully
        localStorage.removeItem("lastDLCode");
        localStorage.removeItem("lastDLState");
      } else {
        setShowLoginButton(true);
        setLoading(false);
      }
    } catch (error) {
      console.error("[LOGIN DEBUG] readTokensFromLocalAndSet: Error occurred", {
        error: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
      });
      // Always stop loading and show login button on error,
      // otherwise the UI can get stuck on the splash screen.
      setShowLoginButton(true);
      setLoading(false);
    }
  }

  const getTokensAndCertsByCodeAndState = async () => {
    console.log("[LOGIN DEBUG] getTokensAndCertsByCodeAndState: Starting...");
    try {
      // Check for OAuth code in URL first - if present, we're in OAuth flow
      const code = getUrlParamByName("code");
      const state = getUrlParamByName("state");

      // CRITICAL: If OAuth code exists, set loading immediately to prevent flash of login button
      if (code) {
        // Validate state parameter to prevent CSRF attacks (per ASAN Digital Login docs)
        const storedState = sessionStorage.getItem("oauthState");
        if (storedState && state !== storedState) {
          console.error("[LOGIN DEBUG] OAuth state mismatch — possible CSRF attack, aborting login");
          sessionStorage.removeItem("oauthState");
          setShowLoginButton(true);
          setLoading(false);
          return;
        }
        sessionStorage.removeItem("oauthState");

        setLoading(true);
        setShowLoginButton(false);
        console.log(
          "[LOGIN DEBUG] getTokensAndCertsByCodeAndState: OAuth code detected, processing..."
        );
      }

      // CRITICAL: Check if we already have valid tokens to prevent duplicate OAuth calls
      const existingToken = sessionStorage.getItem("asanLoginToken");
      const existingIdToken = sessionStorage.getItem("asanLoginIdToken");
      if (existingToken && existingIdToken) {
        const isTokenExpired = checkJwtTokenExpire(existingIdToken);
        if (!isTokenExpired) {
          console.log(
            "[LOGIN DEBUG] getTokensAndCertsByCodeAndState: Valid tokens already exist, skipping OAuth flow"
          );
          await readTokensFromLocalAndSet();
          return;
        }
      }

      const localDLCode = localStorage.getItem("lastDLCode");
      console.log(
        "[LOGIN DEBUG] getTokensAndCertsByCodeAndState: URL parameters",
        {
          hasCode: !!code,
          hasState: !!state,
          codeMatchesLocal: code === localDLCode,
        }
      );

      if (code === localDLCode) {
        await readTokensFromLocalAndSet();
        return;
      }

      if (!code) {
        // No OAuth code in URL - check localStorage for existing session
        console.log(
          "[LOGIN DEBUG] getTokensAndCertsByCodeAndState: No OAuth code, checking localStorage"
        );
        setLoading(true);
        await readTokensFromLocalAndSet();
        return;
      }

      localStorage.setItem("lastDLCode", code);
      localStorage.setItem("lastDLState", state);

      setLoading(true);

      console.log(
        "[LOGIN DEBUG] getTokensAndCertsByCodeAndState: Fetching ASAN tokens..."
      );
      const resp = await getDigitalLoginToken({
        code: code,
      });

      const asanToken =
        resp?.message?.access_token || resp?.data?.message?.access_token;
      const idToken = resp?.message?.id_token || resp?.data?.message?.id_token;
      console.log(
        "[LOGIN DEBUG] getTokensAndCertsByCodeAndState: Tokens received",
        {
          hasAsanToken: !!asanToken,
          hasIdToken: !!idToken,
        }
      );

      if (asanToken) {
        sessionStorage.setItem("asanLoginToken", asanToken);
        sessionStorage.setItem("asanLoginIdToken", idToken);
        console.log(
          "[LOGIN DEBUG] getTokensAndCertsByCodeAndState: Tokens stored, fetching certificates..."
        );
        const certs = await getDigitalLoginCertificates(asanToken);
        console.log(
          "[LOGIN DEBUG] getTokensAndCertsByCodeAndState: Certificates fetched",
          {
            hasCerts: !!certs,
            certificateCount:
              certs?.message?.availableCertificates?.length ||
              certs?.message?.certificates?.length ||
              0,
          }
        );

        let decodedIdToken = jwtDecode(idToken);

        let person = {};

        if (decodedIdToken?.user) {
          person = JSON.parse(JSON.stringify(decodedIdToken.user));
        }

        // setStructureName(person?.structureData?.structureName);
        // setStructureVoen(person?.structureData?.voen);

        if (certs?.message?.loginDetail) {
          person.phoneNumber = certs?.message?.loginDetail?.phoneNumber;
          person.userId = certs?.message?.loginDetail?.userId;
        }

        const availableVoens = [];

        const certificateList =
          certs?.message?.availableCertificates ||
          certs?.message?.certificates ||
          [];

        const certificates = certificateList.filter((x) => x?.structureData);

        if (certificates?.length) {
          for (let i = 0; i < certificates.length; ++i) {
            if (certificates[i]?.structureData) {
              const structureData = {
                certificateNumber: certificates[i]?.serialNumber,
                structureName: certificates[i]?.structureData?.structureName,
                voen: certificates[i]?.structureData?.voen,
              };
              availableVoens.push(structureData);
            }
          }

          setVoens(availableVoens);

          const firstCertificateItem = certificates[0];

          if (firstCertificateItem) {
            setStructureName(
              firstCertificateItem?.structureData?.structureName
            );
            setStructureVoen(firstCertificateItem?.structureData?.voen);
          }
          // if (!checkIsInstitution(person.structureData?.voen)) {
          //   setVoens(availableVoens);
          // }
        } else {
          setRegistrationView(true);
        }

        let voenIsRequired = false;

        if (availableVoens.length) {
          voenIsRequired = true;
          setSubmitBtnDisabled(true);
        }

        setPersonDetails((prevState) => ({
          ...prevState,
          ...person,
        }));
        setVoenIsRequired(voenIsRequired);
        setShowLoginButton(false); // Hide login button, show form
        setLoading(false);
        // Cleanup: OAuth code successfully processed
        localStorage.removeItem("lastDLCode");
        localStorage.removeItem("lastDLState");
      } else {
        // Cleanup on error: no token received
        localStorage.removeItem("lastDLCode");
        localStorage.removeItem("lastDLState");
        enqueueSnackbar(
          intl.formatMessage({
            id: "asan_token_not_found",
          }),
          { variant: "error" }
        );
      }

      setLoading(false);
    } catch (err) {
      console.error(
        "[LOGIN DEBUG] getTokensAndCertsByCodeAndState: Error occurred",
        {
          error: err?.message,
          response: err?.response?.data,
          status: err?.response?.status,
          errorCode: err?.response?.data?.exception?.code,
        }
      );

      // If tokens were saved to sessionStorage before the error
      // (e.g., token exchange succeeded but certificate fetch failed),
      // retry via readTokensFromLocalAndSet instead of showing login button.
      const savedToken = sessionStorage.getItem("asanLoginToken");
      if (savedToken) {
        console.log(
          "[LOGIN DEBUG] getTokensAndCertsByCodeAndState: Tokens exist in sessionStorage, retrying via readTokensFromLocalAndSet"
        );
        try {
          await readTokensFromLocalAndSet();
          return;
        } catch (retryErr) {
          console.error(
            "[LOGIN DEBUG] getTokensAndCertsByCodeAndState: Retry also failed",
            { error: retryErr?.message }
          );
        }
      }

      // Cleanup on error: OAuth code processing failed
      localStorage.removeItem("lastDLCode");
      localStorage.removeItem("lastDLState");
      setLoading(false);
      setSubmitBtnDisabled(true);
      setShowLoginButton(true);
      const errorCode = err?.response?.data?.exception?.code;
      const error = err;
      if (
        errorCode === ERROR_CODES.NO_ASAN_TOKEN ||
        errorCode === ERROR_CODES.TOKEN_EXPIRED
      ) {
        error.message = intl.formatMessage({
          id: "NO_ASAN_TOKEN_PLEASE_LOGIN",
        });
      }
      enqueueSnackbar(
        intl.formatMessage({
          id: error.message,
        }),
        { variant: "error" }
      );
    }
  };

  //#region FAKE STATIC DECODE JWT
  // const decodeJwt = async () => {
  //   try {
  //     setLoading(true);
  //     // asanLoginToken
  //     const asanLoginToken = `eyJqa3UiOiJodHRwczovL2FwaWRpZ2l0YWwubG9naW4uZ292LmF6L3Nzb2F1dGgvb2F1dGgyL2p3a3MiLCJraWQiOiI1Nzg5MTJhYi05NDcxLTQ4MjItYmU4Mi05YjI4N2U1MzViODEiLCJ0eXAiOiJKV1QiLCJhbGciOiJSUzI1NiJ9.eyJzdWIiOiIzODYxNmUyZi0yMWQ0LTRhZWUtODNhZC04YWY3ODkxYTI5OTgiLCJhdWQiOiJkY2VhNDRiYjcxN2I0YTNjOWEzZmU5NDg4OWZmY2U1ZCIsImlzcyI6Imh0dHBzOi8vZGlnaXRhbC5sb2dpbi5nb3YuYXoiLCJleHAiOjE3Mjk3NzQ4NDYsImlhdCI6MTcyOTc3Mzk0NiwidXNlciI6eyJwaW4iOiIxSEVTVDg3IiwibmFtZSI6IkFSxLBGIiwic3VybmFtZSI6Ik3Gj0jGj1JSxo9NT1YiLCJwYXRyb255bWljIjoiVkFTxLBGIE_EnkxVIiwiY2l0aXplbnNoaXAiOiJjaXRpemVuIn0sImp0aSI6IjM3NTExYzQ3LTU0N2UtNDEyZC1iZDIzLWE4OTY5ZGE2MWIyZiJ9.VZzU3PjnLqbXLI4ylpQEbu3RYTluVIC1_DY6CTw4bSV_UlbDHeiLoOzWnYv5_5aqReqHkGcB9qrcm4P0RtZgD6B4P_k-SWqsuJ-FEogPtVi-9VYmC942W9SEs-AxV9-fFuPjo6fwL2CRrteEOZ4L4GyjdNU4T5w2UI5pRS5Da0lij70BHtIzK0577SrTRkv0VXljWZWv8UMte8eJ4lB9crZWwVUngSuZ913-vqcV5FRnXdJZvx-tzlGSfRAj2Z5q4pVzw25XuUOJScTeCkx6PJRF24sOUuGQilQu8gTf9zrijvNZiQnbVZkK_uVRtotCsD2Zv5EEG1CffCSt7xDGrA`;

  //     localStorage.setItem("asanLoginToken", asanLoginToken);

  //     // const certs = await getDigitalLoginCertificates(localDLToken);
  //     const certs = {
  //       status: 200,
  //       message: {
  //         certificates: [
  //           {
  //             structureData: null,
  //             serialNumber: "1fd1a6c0cc6ff43700000049002a",
  //           },
  //         ],
  //         loginDetail: null,
  //       },
  //     };

  //     let decodedIdToken = jwtDecode(asanLoginToken);

  //     let person = {};

  //     if (decodedIdToken?.user) {
  //       person = JSON.parse(JSON.stringify(decodedIdToken.user));
  //     }

  //     if (certs?.message?.loginDetail) {
  //       person.phoneNumber = certs?.message?.loginDetail?.phoneNumber;
  //       person.userId = certs?.message?.loginDetail?.userId;
  //     }

  //     const availableVoens = [];

  //     const certificateList =
  //       certs?.message?.availableCertificates ||
  //       certs?.message?.certificates ||
  //       [];
  //     //   const certificateList = [
  //     //     {
  //     //       structureData: {
  //     //         certificateNumber: 123,
  //     //         structureName: "TEST",
  //     //         voen: "12345678890",
  //     //       },
  //     //       serialNumber: 1,
  //     //     },
  //     //   ];
  //     const certificates = certificateList.filter((x) => x?.structureData);

  //     if (certificates?.length) {
  //       for (let i = 0; i < certificates.length; ++i) {
  //         if (certificates[i]?.structureData) {
  //           const structureData = {
  //             certificateNumber: certificates[i]?.serialNumber,
  //             structureName: certificates[i]?.structureData?.structureName,
  //             voen: certificates[i]?.structureData?.voen,
  //           };
  //           availableVoens.push(structureData);
  //         }
  //       }
  //       setVoens(availableVoens);

  //       const firstCertificateItem = certificates[0];
  //       if (firstCertificateItem) {
  //         setStructureName(
  //           firstCertificateItem?.structureData?.structureName || "TEST"
  //         );
  //         setStructureVoen(firstCertificateItem?.structureData?.voen || "TEST");
  //       }
  //       // if (!checkIsInstitution(person.structureData?.voen)) {
  //       //   setVoens(availableVoens);
  //       // }
  //     } else {
  //       setRegistrationView(true);
  //     }

  //     let voenIsRequired = false;

  //     if (availableVoens.length) {
  //       voenIsRequired = true;
  //       setSubmitBtnDisabled(true);
  //     }

  //     setPersonDetails((prevState) => ({
  //       ...prevState,
  //       ...person,
  //     }));
  //     setVoenIsRequired(voenIsRequired);

  //     setLoading(false);
  //   } catch (err) {
  //     console.error("decodeJwt error: ", err);
  //     setLoading(false);
  //     setSubmitBtnDisabled(true);
  //     const errorCode = err?.response?.data?.exception?.code;
  //     const error = err;
  //     if (errorCode === 1306 || errorCode === 1702) {
  //       error.message = intl.formatMessage({
  //         id: "NO_ASAN_TOKEN_PLEASE_LOGIN",
  //       });
  //     }
  //     enqueueSnackbar(
  //       intl.formatMessage({
  //         id: error?.response?.data.message || error.message,
  //       }),
  //       { variant: "error" }
  //     );
  //   }
  // };
  //#endregion FAKE STATIC DECODE JWT

  // const checkIsInstitution = (structureName) => {
  //   return structureNameList?.some((v) => v?.structureName === structureName);
  // };

  const checkIsInstitution = (voen) => {
    const voenList = Object.values(InstitutionVoens).filter((item) => item);

    const result = Boolean(voenList.includes(voen));
    return result;
  };

  const signWithASAN = async () => {
    console.log("[LOGIN DEBUG] signWithASAN: Starting...", {
      hasPersonDetails: !!personDetails,
      selectedVoen,
      phoneNumber: personDetails?.phoneNumber,
    });
    try {
      console.log(
        "[LOGIN DEBUG] signWithASAN: Fetching business certificate..."
      );
      const cert = await asanService.getBusinessCertificate({
        phoneNumber: personDetails?.phoneNumber,
        personalCode: personDetails?.pin,
        voen: selectedVoen,
      });
      console.log("[LOGIN DEBUG] signWithASAN: Business certificate fetched", {
        hasCertificate: !!cert?.certificate,
      });

      // const signingCertificates = await asanService.getSigningCertificates({
      //   phoneNumber: personDetails?.phoneNumber,
      //   userId: personDetails?.userId,
      // });

      // console.log("signingCertificates", signingCertificates);
      // const certificatesObj = signingCertificates?.certificates;
      // const firstCertKey = certificatesObj ? Object.keys(certificatesObj) : [];

      let selectedCert = cert?.certificate;

      const selectedVoenObj = voens.find((v) => v.voen == selectedVoen);
      console.log("[LOGIN DEBUG] signWithASAN: Selected VOEN object", {
        hasSelectedVoenObj: !!selectedVoenObj,
        certificateNumber: selectedVoenObj?.certificateNumber,
      });

      console.log("[LOGIN DEBUG] signWithASAN: Initiating file signing...");
      const resp = await asanService.signFiles({
        phoneNumber: personDetails.phoneNumber,
        userId: personDetails.userId,
        certIdentifier:
          personDetails?.selectedCertNumber ||
          selectedVoenObj?.certificateNumber,
        certificate: selectedCert,
      });
      console.log("[LOGIN DEBUG] signWithASAN: Sign files response", {
        hasVerificationCode: !!resp?.verificationCode,
        transactionId: resp?.transactionId,
      });

      if (resp?.verificationCode) {
        setPin2(resp.verificationCode);
        setAsanModalState(true);
        let localSeconds = TIMER_CONSTANTS.INITIAL_SECONDS;
        const elapsed = () => TIMER_CONSTANTS.INITIAL_SECONDS - localSeconds;
        const interval = setInterval(async () => {
          try {
            localSeconds--;
            setSeconds(localSeconds);
            let result;
            // ASAN docs: wait 10-15s before first poll, then every 2-10s
            const pastInitialDelay = elapsed() >= TIMER_CONSTANTS.INITIAL_POLL_DELAY_SECONDS;
            const onCheckTick = !((elapsed() - TIMER_CONSTANTS.INITIAL_POLL_DELAY_SECONDS) % TIMER_CONSTANTS.STATUS_CHECK_INTERVAL);
            if (pastInitialDelay && onCheckTick) {
              // signFiles requires getSignFileStatus (not getSignStatus).
              // getSignStatus returns a raw signature; getSignFileStatus
              // returns the full signed adoc container needed by DVX.
              result = await asanService.getSignFileStatus({
                transactionId: String(resp.transactionId),
                hash: resp.hash,
                certificate: selectedCert,
                container: resp.container,
                voen: selectedVoen,
                phoneNumber: personDetails?.phoneNumber,
                userId: personDetails?.userId,
                stakeholderId: selectedStakeholderId || null,
              });
              if (result.status === AsanSignResponseCodes.SUCCESS) {
                setAsanSignVerified(result);
                // Store signed adoc container from getSignFileStatus,
                // not the unsigned template from initial signFiles response.
                // DVX validates the adoc signature — sending the unsigned
                // resp.container causes "sənədə müdaxilə" rejection.
                localStorage.setItem(
                  "signinStatusData",
                  JSON.stringify({
                    ...resp,
                    container: result.signature,
                  })
                );

                handleClose(true);
                setSeconds(0);
              }
              if (
                result.status === AsanSignResponseCodes.CANCELLED ||
                result.status === AsanSignResponseCodes.SIM_ERROR ||
                result.status === AsanSignResponseCodes.ERROR
              ) {
                enqueueSnackbar(
                  intl.formatMessage({
                    id: result.status,
                  }),
                  {
                    variant: "error",
                  }
                );
                setSeconds(0);
              }
            }
          } catch (err) {
            enqueueSnackbar(
              intl.formatMessage({
                id: err?.response?.data.message || err.message,
              }),
              { variant: "error" }
            );
            setSeconds(10000);
          }
        }, TIMER_CONSTANTS.CHECK_INTERVAL_MS);
        timerIntervalRef.current = interval;
      } else {
        enqueueSnackbar(
          intl.formatMessage({
            id: "Verification code not fond",
          }),
          { variant: "error" }
        );
      }
    } catch (err) {
      console.error("[LOGIN DEBUG] signWithASAN: Error occurred", {
        error: err?.message,
        response: err?.response?.data,
        status: err?.response?.status,
      });
      setSeconds(0);

      const errorMessage =
        err?.response?.data === "error.no_signing_certificate"
          ? "error.no_signing_certificate"
          : err?.response?.data?.message ||
            err?.message ||
            "Sign with ASAN error";

      enqueueSnackbar(
        intl.formatMessage({
          id: errorMessage,
        }),
        { variant: "error" }
      );
    }
  };

  const handleSubmit = async () => {
    console.log("[LOGIN DEBUG] handleSubmit: Starting...", {
      hasRegisteredStakeholders: registeredStakeholders?.length > 0,
      stakeholderCount: registeredStakeholders?.length,
      selectedVoen,
      selectedStakeholderId,
    });
    try {
      if (!(registeredStakeholders?.length || selectedVoen)) {
        console.log(
          "[LOGIN DEBUG] handleSubmit: No stakeholders or VOEN, showing registration view"
        );
        setRegistrationView(true);
        return;
      }
      setLoading(true);

      let dataVoen = voens.find((v) => v.voen === selectedVoen);

      setSelectedCertificate(dataVoen);
      dispatch(setVoen(selectedVoen));

      (async function checkRegistrationObjects() {
        console.log(
          "[LOGIN DEBUG] handleSubmit: Checking registration objects",
          {
            objectsCount: objects?.length,
          }
        );
        if (objects?.length === 0) {
          console.log("[LOGIN DEBUG] handleSubmit: No objects found");
          enqueueSnackbar(
            intl.formatMessage({
              id: "err.objectMsg",
            }),
            {
              variant: "error",
            }
          );
        } else {
          if (objects?.length > 1) {
            console.log(
              "[LOGIN DEBUG] handleSubmit: Multiple objects found, fetching stakeholder list..."
            );
            let response = await getObject(selectedVoen);
            console.log(
              "[LOGIN DEBUG] handleSubmit: Stakeholder list fetched",
              {
                stakeholderCount: response?.data?.stakeholders?.length,
              }
            );
            const stakeholderList = response?.data?.stakeholders || [];

            if (stakeholderList.length > 0) {
              console.log(
                "[LOGIN DEBUG] handleSubmit: Fetching token for stakeholder",
                {
                  stakeholderId:
                    selectedStakeholderId || stakeholderList[0]._id,
                }
              );
              let resp = await dispatch(
                fetchToken({
                  id: selectedStakeholderId || stakeholderList[0]._id,
                })
              );
              console.log("[LOGIN DEBUG] handleSubmit: Token fetch response", {
                hasData: !!resp?.data,
                isActive: resp?.data?.active,
              });

              await dispatch(
                fetchNotifications(
                  selectedStakeholderId || stakeholderList[0]._id
                )
              );
              if (resp?.data && resp?.data?.active) {
                history.replace("/stakeholder/cabinet/home");
              } else if (resp?.data && !resp?.data?.active) {
                history.replace("/stakeholder/new");
              } else {
                console.log(
                  "Login: fetchToken response data not found: ",
                  resp
                );
                enqueueSnackbar(
                  intl.formatMessage({
                    id: resp.message,
                  }),
                  {
                    variant: "error",
                  }
                );
              }
            } else {
              setRegistrationView(true);
            }
          } else {
            setRegistrationView(true);
          }
        }
      })();

      // if (!checkIsInstitution(dataVoen?.voen)) {
      //   // dispatch(setStructureName(dataVoen?.structureName));
      // }
      setLoading(false);
    } catch (err) {
      console.error("[LOGIN DEBUG] handleSubmit: Error occurred", {
        error: err?.message,
        response: err?.response?.data,
        status: err?.response?.status,
      });
      enqueueSnackbar(
        intl.formatMessage({
          id: err?.response?.data?.message || err.message,
        }),
        { variant: "error" }
      );
      setLoading(false);
    }
  };

  function clearLocalStorageAndRedirect() {
    return () => {
      window.localStorage.clear();
      window.location.href = "https://data.tourism.gov.az/";
    };
  }

  // const handleLoginLocal = () => {
  //   decodeJwt(true).then((data) => {});

  //   const resultStatus = "success";
  //   if (resultStatus === "success") {
  //     const resp = {
  //       container:
  //         "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",
  //       transactionId: "9131444074040938846",
  //       hash:
  //         "30429C5CC1B71937F1633DDB26A47A8DC4BDDCF79A619BC602484326FA46E8766A57358054E4255CDAB2C4644CD6369B018E5B6D4CD2141BE62F275ED80541A5",
  //       verificationCode: "1573",
  //     };
  //     localStorage.setItem("signinStatusData", JSON.stringify(resp));

  //     handleClose(true);
  //   }
  // };

  //   useEffect(() => {
  //     console.log("Login: voens: ", voens);
  //     console.log("Login: structureVoen: ", structureVoen);
  //     console.log("Login: registeredStakeholders: ", registeredStakeholders);
  //     console.log("Login: personDetails", personDetails);
  //   }, [voens, structureVoen, registeredStakeholders, personDetails]);

  const getTaxesInfoByVoen = async () => {
    console.log("[getTaxesInfoByVoen DEBUG]: Starting...");
    const selectedVoen = localStorage.getItem("selectedVoen");
    const localData = localStorage.getItem("signinStatusData");
    let parsedData = localData ? JSON.parse(localData) : null;
    console.log(
      "[getTaxesInfoByVoen DEBUG]: Retrieved data from localStorage",
      {
        hasSelectedVoen: !!selectedVoen,
        selectedVoen,
        hasLocalData: !!localData,
        hasContainer: !!parsedData?.container,
      }
    );
    if (selectedVoen && localData && parsedData?.container) {
      const obj = {
        base64Content: parsedData.container,
        fileType: "adoc",
        voenOrPin: selectedVoen,
      };
      console.log("[getTaxesInfoByVoen DEBUG]: Fetching tax object data...");
      try {
        // setTaxPayerObjLoading(true);
        let taxesObjData = await getTaxesObject(obj);
        console.log("[getTaxesInfoByVoen DEBUG]: Tax object data received", {
          hasTourismDTOList: !!taxesObjData?.data?.tourismDTOList,
          tourismListLength: taxesObjData?.data?.tourismDTOList?.length,
          hasInvalidParams: taxesObjData?.data?.invalidParams != null,
          detail: taxesObjData?.data?.detail,
        });
        // if (taxesObjData?.data?.invalidParams == null) {
        //   setDetailEr(taxesObjData.data.detail);
        // }

        // if (taxesObjData?.data?.tourismDTOList) {
        //   setTaxPayerObjData(taxesObjData.data.tourismDTOList);
        // }
        // setTaxPayerObjLoading(false);
      } catch (error) {
        // setTaxPayerObjLoading(false);
        console.error("[getTaxesInfoByVoen DEBUG]: getTaxesObject error", {
          error: error?.message,
          response: error?.response?.data,
          status: error?.response?.status,
        });
      }

      console.log("[getTaxesInfoByVoen DEBUG]: Fetching tax payer info...");
      try {
        // setTaxPayerInfoLoading(true);
        let taxesPayerData = await getTaxesPayer(obj);
        console.log("[getTaxesInfoByVoen DEBUG]: Tax payer info received", {
          taxesPayerData: taxesPayerData?.data,
          hasLegalAddressData: !!taxesPayerData?.data?.legalPayerEnt
            ?.legalAddressRelationEnt?.addressEnt,
        });
        // if (
        //   taxesPayerData?.data?.legalPayerEnt?.legalAddressRelationEnt
        //     ?.addressEnt
        // ) {
        //   setTaxPayerInfo(
        //     taxesPayerData.data.legalPayerEnt.legalAddressRelationEnt.addressEnt
        //   );
        // }
        // setTaxPayerInfoLoading(false);
      } catch (error) {
        // setTaxPayerInfoLoading(false);
        console.error("[getTaxesInfoByVoen DEBUG]: getTaxesPayer error", {
          error: error?.message,
          response: error?.response?.data,
          status: error?.response?.status,
        });
      }
    } else {
      console.log("[getTaxesInfoByVoen DEBUG]: Missing required data", {
        hasSelectedVoen: !!selectedVoen,
        hasLocalData: !!localData,
        hasContainer: !!parsedData?.container,
      });
    }
  };

  useEffect(() => {
    async function checkRegistrationObjects() {
      try {
        let response = await getObject(selectedVoen);

        setRegisteredStakeholders(response?.data?.stakeholders);
      } catch (error) {
        console.error(
          "Login: useEffect checkRegistrationObjects error: ",
          error?.response || error
        );
      }
    }

    if (selectedVoen) {
      checkRegistrationObjects();

      // DEBUG: Fetch taxes info by VOEN
      getTaxesInfoByVoen();
    }
  }, [selectedVoen]);

  let personFullName = personDetails?.name
    ? `${personDetails?.name} ${personDetails?.surname} ${personDetails?.patronymic}`
    : "-";

  // Login Button UI Component
  if (showLoginButton === true) {
    return (
      <Grid
        container
        justify="center"
        alignContent="center"
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        <Grid item md={5} sm={10} xs={11}>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "20px",
              padding: "60px 40px",
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              textAlign: "center",
            }}
          >
            <div style={{ marginBottom: "30px" }}>
              <img
                src={bg}
                alt="Tourism Logo"
                style={{
                  maxWidth: "300px",
                  width: "100%",
                  marginBottom: "20px",
                }}
              />
            </div>

            <h2
              style={{
                color: "#333",
                marginBottom: "15px",
                fontSize: "28px",
                fontWeight: "600",
              }}
            >
              <FormattedMessage id="welcome" defaultMessage="Xoş gəlmisiniz" />
            </h2>

            <p
              style={{
                color: "#666",
                marginBottom: "40px",
                fontSize: "16px",
                lineHeight: "1.6",
              }}
            >
              <FormattedMessage
                id="login.description"
                defaultMessage="Kabinetə daxil olmaq üçün mygov ID istifadə edin"
              />
            </p>

            {/* Digital Login Logo Card */}
            <div
              style={{
                marginBottom: "20px",
                padding: "15px 20px",
                // backgroundColor: "#f8f9fa",
                // borderRadius: "10px",
                // border: "2px solid #e9ecef",
                display: "inline-block",
              }}
            >
              <img
                src={mygovidlogo}
                alt="Digital Login"
                style={{
                  height: "42px",
                  maxWidth: "100%",
                  display: "block",
                }}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                type="button"
                onClick={handleAsanLogin}
                style={{
                  minWidth: "280px",
                  maxWidth: "350px",
                  height: "52px",
                  fontSize: "16px",
                  fontWeight: "600",
                  borderRadius: "10px",
                  backgroundColor: "#543195",
                  border: "none",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  textTransform: "none",
                  padding: "12px 24px",
                  boxShadow: "0 4px 15px rgba(84, 49, 149, 0.3)",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#6b3fb5";
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 20px rgba(84, 49, 149, 0.4)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "#543195";
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 15px rgba(84, 49, 149, 0.3)";
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ flexShrink: 0, marginRight: "8px" }}
                >
                  <path
                    d="M12 14.5V16.5M7 10.0288C7.47142 10 8.05259 10 8.8 10H15.2C15.9474 10 16.5286 10 17 10.0288M7 10.0288C6.41168 10.0647 5.99429 10.1455 5.63803 10.327C5.07354 10.6146 4.6146 11.0735 4.32698 11.638C4 12.2798 4 13.1198 4 14.8V16.2C4 17.8802 4 18.7202 4.32698 19.362C4.6146 19.9265 5.07354 20.3854 5.63803 20.673C6.27976 21 7.11984 21 8.8 21H15.2C16.8802 21 17.7202 21 18.362 20.673C18.9265 20.3854 19.3854 19.9265 19.673 19.362C20 18.7202 20 17.8802 20 16.2V14.8C20 13.1198 20 12.2798 19.673 11.638C19.3854 11.0735 18.9265 10.6146 18.362 10.327C18.0057 10.1455 17.5883 10.0647 17 10.0288M7 10.0288V8C7 5.23858 9.23858 3 12 3C14.7614 3 17 5.23858 17 8V10.0288"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>
                  <FormattedMessage
                    id="login.with.asan"
                    defaultMessage="Kabinetə daxil ol"
                  />
                </span>
              </Button>
            </div>

            {/* Language Selector - Minimal Design */}
            <div
              style={{
                marginTop: "32px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <LanguageSelectorDropdown />
            </div>
          </div>
        </Grid>
      </Grid>
    );
  }

  return loading ? (
    <LayoutSplashScreen />
  ) : showLoginButton === null ? (
    // Still checking authentication state
    <LayoutSplashScreen />
  ) : registrationView ? (
    <Registration
      personDetails={personDetails}
      voens={voens}
      voenIsRequired={voenIsRequired}
      selectedCertificate={selectedCertificate}
      setSelectedCertificate={setSelectedCertificate}
      handleBackButton={() => {
        setOpen(false);
        setRegistrationView(false);
        window.location.reload();
      }}
      history={history}
    />
  ) : (
    <Grid
      container
      justify="center"
      alignContent="center"
      className={classesMaterial.gridHV}
    >
      <Grid item md={6} sm={10}>
        <div style={{ margin: "50px 20px" }}>
          <div className={`${classesMaterial.cardW} `}>
            <div className="card-header justify-content-end border-0">
              <div className="d-flex align-items-center justify-content-end">
                <LanguageSelectorDropdown reg={true} />
              </div>
            </div>
            <div
              className={`card-body ${classesMaterial.cardBody} pt-2 bg-white d-flex align-items-start`}
            >
              <div>
                <div className="d-flex align-items-center w-100">
                  <img src={bg} alt="" width="100%" className="m-auto" />
                </div>
              </div>
              <div className="w-50">
                <FormControl
                  size="small"
                  variant="outlined"
                  className={classesMaterial.formControl}
                  style={{
                    margin: "10px 0",
                    width: "100%",
                  }}
                >
                  <OutlinedInput
                    readOnly
                    value={personDetails?.name ? personFullName : "-"}
                    labelWidth={pinLabelWidth}
                  />
                </FormControl>

                {voens.length ? (
                  <div className="d-flex align-items-center">
                    <FormControl
                      size="small"
                      variant="outlined"
                      className={classesMaterial.formControl}
                      style={{
                        margin: "10px 0",
                        width: "100%",
                      }}
                    >
                      <InputLabel ref={voenInputLabel} htmlFor="voen">
                        <FormattedMessage id="select_stakeholder" />
                      </InputLabel>
                      <Select
                        value={selectedVoen}
                        onChange={(e) => {
                          localStorage.setItem("selectedVoen", e.target.value);
                          setSelectedVoen(e.target.value);
                          setStructureVoen(e.target.value);
                          setSubmitBtnDisabled(false);
                          if (checkIsInstitution(e.target.value)) {
                            setAgreement(false);
                          }
                        }}
                        input={
                          <OutlinedInput
                            labelWidth={voenLabelWidth}
                            name="voen"
                            id="outlined-voen-simple"
                          />
                        }
                      >
                        {voens.map((v) => (
                          <MenuItem key={v.voen} value={v.voen}>
                            {v.structureName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Tooltip
                      placement="right"
                      title={
                        <span
                          style={{
                            fontSize: "13px",
                          }}
                        >
                          <FormattedMessage id="reg.tooltip.structureName" />
                        </span>
                      }
                    >
                      <IconButton>
                        <Info />
                      </IconButton>
                    </Tooltip>
                  </div>
                ) : null}

                {registeredStakeholders &&
                registeredStakeholders?.length > 0 ? (
                  <FormControl
                    size="small"
                    variant="outlined"
                    className={classesMaterial.formControl}
                    style={{
                      margin: "10px 0",
                      width: "100%",
                    }}
                  >
                    <InputLabel
                      ref={registeredStakeholdersInputLabel}
                      htmlFor="voen"
                    >
                      <FormattedMessage id="registered_stakeholders" />
                    </InputLabel>
                    <Select
                      value={selectedStakeholderId}
                      onChange={(e) => {
                        setSelectedStakeholderId(e.target.value);
                        setSubmitBtnDisabled(false);
                      }}
                      input={
                        <OutlinedInput
                          labelWidth={registeredStakeholdersLabelWidth}
                          name="stakeholders"
                          id="outlined-voen-simple"
                        />
                      }
                    >
                      {registeredStakeholders?.map((s) => (
                        <MenuItem key={s._id} value={s._id}>
                          {s?.brandName || s?.name}
                          {" - "}
                          {s?.industryCategory?.name?.[intl.locale]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : null}

                {structureVoen && !checkIsInstitution(structureVoen) ? (
                  <FormControlLabel
                    checked={confirmCheck}
                    control={<Switch color="primary" />}
                    label={<FormattedMessage id="key_term_and_references" />}
                    disabled={submitBtnDisabled}
                    labelPlacement="end"
                    onChange={(e) => handleClickOpen(e)}
                  />
                ) : null}

                <div className="card-toolbar d-flex justify-content-between mt-8">
                  <Button
                    style={{
                      textTransform: "capitalize",
                    }}
                    variant="outlined"
                    color="default"
                    size="small"
                    className={classesMaterial.button}
                    onClick={() =>
                      (window.location =
                        process.env.REACT_APP_DIGITAL_LOGIN_URL)
                    }
                  >
                    <FormattedMessage id="button.back" />
                  </Button>

                  <ThemeProvider theme={theme}>
                    <Button
                      variant="contained"
                      size="small"
                      className={
                        agreement
                          ? classesMaterial.btnContinueDisable
                          : classesMaterial.btnContinue
                      }
                      onClick={() => handleSubmit()}
                      disabled={agreement}
                    >
                      <FormattedMessage id="button.continue" />
                    </Button>
                  </ThemeProvider>

                  {/* {createNewBtnView ? (
                    <Button
                      id="contunie1"
                      style={{
                        textTransform: "capitalize",
                      }}
                      variant="contained"
                      color="primary"
                      size="small"
                      className={classesMaterial.button}
                      onClick={() => setRegistrationView(true)}
                      startIcon={<AddCircleOutline />}
                    >
                      <FormattedMessage id="button.createNew" />
                    </Button>
                  ) : null} */}

                  {/* <Button
                    variant="outlined"
                    color="default"
                    size="small"
                    className={classesMaterial.button}
                    onClick={handleLoginLocal}
                  >
                    <FormattedMessage id="button.login" />
                  </Button> */}

                  {/* <ThemeProvider theme={theme}>
                    {!(voens?.length > 0) ? (
                      <Button
                        id="contunie2"
                        variant="contained"
                        size="small"
                        className={
                          agreement
                            ? classesMaterial.btnContinueDisable
                            : classesMaterial.btnContinue
                        }
                        onClick={() => handleSubmit()}
                        disabled={agreement}
                      >
                        <FormattedMessage id="button.continue" />
                      </Button>
                    ) : null}

                    {voens?.length > 0 ? (
                      <div>
                        <Button
                          id="contunie3"
                          variant="contained"
                          size="small"
                          className={
                            agreement
                              ? classesMaterial.btnContinueDisable
                              : classesMaterial.btnContinue
                          }
                          disabled={agreement}
                          onClick={() => handleSubmit()}
                        >
                          <FormattedMessage id="button.continue" />
                        </Button>
                      </div>
                    ) : null}
                  </ThemeProvider> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Grid>

      {/* Key do you agree modal */}
      <Dialog
        open={open}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle>
          <FormattedMessage id="key_do_you_agree" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-slide-description"
            style={{ color: "unset" }}
          >
            <span
              style={{
                fontWeight: "bold",
                marginLeft: "15%",
              }}
            >
              {" "}
              Turizm İnformasiya Sistemindən istifadə qaydaları və şərtləri
            </span>{" "}
            <br></br>
            <span style={{ marginLeft: "3%" }}> 1. Ümumi müddəalar </span>
            <br />
            1.1. Turizm informasiya sistemi (bundan sonra TİS) turizm
            reyestrinin aparılması məqsədi ilə yaradılmışdır və Dövlət Turizm
            Agentliyi tərəfindən idarə edilir.
            <br /> 1.2. Bu istifadə qaydaları və şərtləri data.tourism.gov.az
            portalı (bundan sonra Portal) vasitəsi ilə Turizm İnformasiya
            Sistemindən istifadə qaydalarını, istifadəçilərin və operatorun
            məsuliyyətini müəyyən edir.
            <br />
            1.3. Portalda Turizm reyestrinə daxil edilən turizm sənayesi
            subyektləri barədə açıq məlumatları əldə edilməsi, habelə Turizm
            Reyestrinə məlumatların daxil edilməsi və Portal vasitəsi ilə təqdim
            edilən elektron xidmətlərdən istifadə edilməsi bu istifadə qaydaları
            və şərtlərinin predmetini təşkil edir.
            <br /> 1.4. Bu istifadə qaydaları və şərtlərində istifadə edilən
            anlayışlar aşağıdakı mənaları ifadə edir: <br />
            <span style={{ fontWeight: "bold" }}>• Turizm reyestri</span> -
            turizm ehtiyatları, turizm məhsulları və turizm sənayesi subyektləri
            haqqında məlumat bazasını, turizm sənayesi və turizm fəaliyyəti
            barədə statistik məlumatları və həmin məlumatların monitorinqinin
            nəticələrini özündə əks etdirən elektron dövlət informasiya
            ehtiyatı;
            <br />{" "}
            <span style={{ fontWeight: "bold" }}>
              • Turizm sənaye subyektləri
            </span>{" "}
            - səyahətin məqsədindən asılı olaraq turistlərə göstərilən
            yerləşdirmə, nəqliyyat (nəqliyyat vasitələrinin turistlərə kirayəyə
            verilməsi daxil olmaqla), turagent, turoperator, turizm bələdçisi,
            ictimai iaşə (qidalanma), əyləncə, idman-sağlamlıq, mədəni, işgüzar
            tədbirlərin təşkilini və turistlərin tələb və ehtiyaclarının
            ödənilməsini təmin edən digər turizm xidmətləri göstərən hüquqi və
            fiziki şəxslər, habelə turizm-təbliğat fəaliyyətini həyata keçirən,
            turizm təhsili və təlimi xidmətləri göstərən hüquqi şəxslər, turizm
            sənaye assosiasiyaları;
            <br />{" "}
            <span style={{ fontWeight: "bold" }}> turizm xidmətləri</span> –
            səyahətin məqsədindən asılı olaraq turistlərə ödənişli əsaslarla
            göstərilən yerləşmə, nəqliyyat (nəqliyyat vasitələrinin turistlərə
            kirayəyə verilməsi daxil olmaqla), turagent, turoperator, turizm
            bələdçisi, ictimai iaşə (qidalanma), əyləncə, idman-sağlamlıq,
            mədəni, işgüzar tədbirlərin təşkili xidmətləri və turistlərin tələb
            və ehtiyaclarının ödənilməsini təmin edən digər xidmətlər.
            <br /> 1.5.Bu istifadə qaydaları və şərtlərində istifadə edilən
            digər anlayışlar{" "}
            <u>
              <a
                href="https://e-qanun.az/framework/49162"
                style={{ cursor: "pointer" }}
              >
                “Turizm haqqında”
              </a>
            </u>{" "}
            Azərbaycan Respublikasının Qanunu və
            <u>
              <a
                href="https://e-qanun.az/framework/44295"
                style={{ cursor: "pointer" }}
              >
                “Turizm reyestrinin aparılması qaydası”
              </a>
            </u>{" "}
            nda göstərilən mənaları ifadə edir.
            <br />
            <span style={{ fontWeight: "bold" }}>
              2. TİS-dən istifadə{" "}
            </span>{" "}
            <br /> <span style={{ fontWeight: "bold" }}>2.1.</span> TİS-dən
            istifadə edən şəxslər aşağıdakı kateqoriyalara bölünür:
            <br /> <span style={{ fontWeight: "bold" }}> 2.1.1</span> Əhatə
            dairəsi Azərbaycan Respublikası Nazirlər Kabinetinin 2020-ci il
            tarixli 16 nömrəli qərarı ilə təsdiq edilmiş
            <u>
              <a href="https://e-qanun.az/framework/44295">
                {" "}
                Turizm reyestrinin aparılması qaydasının 3.1.3-cü yarımbəndi ilə
                müəyyən edilən turizm sənaye subyektləri;
              </a>
            </u>{" "}
            <br /> <span>2.1.2.</span> Portalda yerləşdirilmiş açıq hesab edilən
            məlumatlardan, turizm sənaye subyektləri tərəfindən təqdim edilən
            xidmətlərdən, müxtəlif modullar vasitəsi ilə təqdim edilən
            xidmətlərdən istifadə edən şəxslər;
            <br /> <span style={{ fontWeight: "bold" }}> 2.2.</span> Operatorun
            səlahiyyətli şəxsləri istifadəçilər tərəfindən reyestrə daxil
            edilmiş məlumatların mötəbərliyinin və həqiqiliyinin yoxlanılması
            məqsədi ilə monitorinqlər keçirə bilər. <br /> <span>2.3.</span>
            Turizm sənayesi subyektləri aşağıdakı formalarda TİS-dən istifadə
            edir:
            <br /> <span>
              2.3.1. Turizm reyestrində qeydiyyatdan keçmək;
            </span>{" "}
            <br />
            <span>
              2.3.2. Turizm reyestri vasitəsi ilə biznes obyektinin fəaliyyət
              istiqaməti, təyinatı və forması üzrə müvafiq qaydada
              təsnifləşdirilməsi;
            </span>
            <br />
            <span>2.3.3. Reytinq sisteminə daxil olmaq;</span>
            <br />
            <span>
              2.3.4. Fəaliyyəti, təqdim etdiyi xidmətlər, habelə təşkil etdiyi
              tədbirlər barədə məlumatları təqdim etmək;
            </span>
            <br />
            <span>
              2.3.5. Digər biznes subyektləri tərəfindən təqdim edilən xidmətlər
              barədə məlumatlar əldə etmək;
            </span>
            <br />
            <span>
              2.3.6. Dövlət Turizm Agentliyi və ya tabeliyindəki struktur
              qurumlar tərəfindən təqdim edilən elektron xidmətlərdən istifadə
              etmək;
            </span>
            <br />
            <span style={{ fontWeight: "bold" }}>
              2.4. Siz Turizm informasiya sisteminə məlumatlar daxil etməklə
              operatorun həmin məlumatların düzgünlüyünü yoxlamaq, hər hansı
              şübhəli hal yarandığı təqdirdə təsdiq etməmək, məlumatların
              yoxlanılması üçün əlavə sənədlər tələb etmək, ehtiyac olduğu
              təqdirdə obyektə yerində baxış keçirmək hüququna razılıq
              verirsiniz.
            </span>
            <br />
            <span style={{ fontWeight: "bold" }}>
              3. Turizm Reyestrində qeydiyyat:
            </span>{" "}
            <br /> <span style={{ fontWeight: "bold" }}>3.1.</span>
            Portal vasitəsi ilə turizm reyestrində qeydiyyat: <br /> 3.2. Turizm
            Reyestrində qeydiyyat biznes əsaslı (təsərrüfat obyekti) aparılır.
            <br />
            3.3. Turizm Reyestrində qeydiyyatdan keçmək üçün TİS-ə Vahid Giriş
            Sistemi vasitəsi ilə daxil olan hər bir fərdi sahibkar/hüquqi şəxsin
            Dövlət Vergi Xidmətinin Avtomatlaşdırılmış Vergi İnformasiya
            Sistemində (AVİS) bəyan etdiyi fəaliyyət növünün{" "}
            <u>turizm sahəsi üzrə fəaliyyət növlərinin siyahısına</u>
            uyğun olmasının yoxlanılması üçün AVİS-ə sorğu göndərilir. <br />
            3.4. Yalnız, turizm sənayesi fəaliyyət növü üzrə vergi ödəyicisi
            olan fərdi sahibkar/hüquqi şəxslər Turizm Reyestrindən qeydiyyatdan
            keçə bilərlər.
            <i style={{ color: "black" }}>
              <br />
              Qeyd: İlkin mərhələ üzrə turizm reyestrində yalnız, turoperator və
              turagent fəaliyyəti, yerləşmə xidmətləri və bələdçilik fəaliyyəti
              ilə məşğul olan turizm sənayesi subyektləri qeydiyyatdan keçmək
              imkanına malikdir. Növbəti mərhələlərdə, digər sənaye
              subyektlərinin sistemə daxil olmasına şərait yaradılacaqdır.
            </i>
            <br />
            3.5. Qeydiyyat formasının doldurulması zamanı turizm sənaye
            subyektləri turizm xidmətlərini təqdim etdiyi hər bir obyektə dair
            məlumatları təqdim etməlidir. Qeydiyyatdan keçmək üçün, hər bir
            sahibkarlıq subyektinin fəaliyyətində olan obyektlərin təsnifatının
            aparıla bilinməsi üçün, fəaliyyət göstərilən obyektlərə dair
            məlumatlar (obyekt sayı, obyekt kodu, ünvanı və s.) tələb edilir.{" "}
            <br />
            3.6. Qeydiyyat zamanı doldurulan məlumatların həqiqiliyi AVİS
            vasitəsi ilə yoxlanılır.
            <br />
            3.7. Eyni anda müxtəlif fəaliyyət növündə turizm xidməti(yerləşmə,
            turoperator və turagent, bələdçilik) təqdim edirsizsə hər bir
            fəaliyyət növü üzrə biznesinizin (təsərrüfat obyektlərini)
            qeydiyyatı üçün hər bir obyektə münasibətdə fərdi qaydada qeydiyyat
            formasını doldurmalısız.
            <br />{" "}
            <span style={{ fontWeight: "bold" }}>
              4. Turizm sənaye subyektlərinin təsnifatı
            </span>{" "}
            <br />
            <span>4.1.</span>
            Turizm sənaye subyektləri yaradılan elektron kabinet daxilində 
            fəaliyyət istiqaməti üzrə qeydiyyatdan keçirdiyi hər bir obyektin
            növünün təyin edilməsinə müraciət etməklə, təsnifləşdirmənin həyata
            keçirilə bilməsi üçün tələb edilən məlumatların daxil edilməsinə,
            istifadə edilməsi və emal edilməsinə razılıq verir. <br />{" "}
            <span>4.2.</span> Turizm reyestri vasitəsi ilə təsnifatlaşdırmaya
            müraciət etməklə məlumatların doğruluğuna şübhə yarandığı hallarda,
            habelə, məlumatların yerində təsdiq edilməsinə ehtiyac yarandığı
            hallarda operatorun səlahiyyətli əməkdaşları tərəfindən obyektə
            yerində baxış keçirilməsinə razılıq verilmiş hesab edilir.
            <br />{" "}
            <span style={{ fontWeight: "bold" }}>
              5. Reytinqin aparılması{" "}
            </span>{" "}
            <br /> <span style={{ fontWeight: "bold" }}> 5.1. </span> Turizm
            sənayesi subyektlərinin reytinq sistemi Turizm Keyfiyyət Proqramı
            əsasında təsis edilən müvafiq fərqləndirici nişanların verilməsi
            üçün müraciətin edilməsi və ya həmin nişanlar çərçivəsində müəyyən
            edilən müvafiq sualların cavablandırılması üçün subyektlər
            tərəfindən TİS-ə daxil edilən məlumatlar əsasında həyata keçirilir.
            <br />{" "}
            <span>
              {" "}
              5.2. Reytinqin aparılması üçün daxil edilən hər bir məlumat və
              təsdiqedici sənədlər TİS-in məlumat anbarına ötürülür. Təqdim
              edilən sənədlərin həqiqiliyi və düzgünlüyü yoxlanıldıqdan sonra
              operator tərəfindən təsdiq edilir.{" "}
            </span>
            <br /> 5.3. Sistemə daxil edilən məlumatların həqiqiliyi və
            düzgünlüyünə şübhə yarandığı hallarda operator tərəfindən
            məlumatların dəqiqləşdirilməsi üçün turizm sənaye subyektinə
            bildiriş göndərilə bilər.
            <br />
            <span style={{ fontWeight: "bold" }}>
              6. Turizm Reyestrdən elektron çıxarışların qüvvəsi:{" "}
            </span>{" "}
            <br />
            6.1. İstifadəçilər istənilən zaman elektron kabinetində
            yerləşdirilən məlumatları sistem tərəfindən təsdiq edilən çıxarışı
            unikal sistem nömrəsi verilmiş elektron qaydada yükləyə bilərlər.
            Turizm Reyestrdən elektron çıxarışlar 6 ay müddətində etibarlıdır.
            <br />
            <span style={{ fontWeight: "bold" }}>
              7. Turizm Reyestrinə müraciətlər
            </span>{" "}
            <br /> 7.1. Portalın istifadəçiləri portaldan istifadə və TİS-dən
            təqdim edilən elektron xidmətlər ilə əlaqədar{" "}
            <u>
              <a href="support@tourism.gov.az">support@tourism.gov.az</a>
            </u>{" "}
            emaili vasitəsi ilə və ya 848 qaynar xətt nömrəsinə zəng etmək ilə
            müraciət edə bilərlər. <br /> <br />
            <span style={{ fontWeight: "bold" }}>
              {" "}
              8. TİS istifadəçilərinin məsuliyyəti
            </span>
            <br />
            8.1. Əqli mülkiyyət hüququnu pozan, hədə, böhtan, pornoqrafik
            xarakter daşıyan, təhqiramiz, nalayiq, təhrikçi materialların və ya
            Azərbaycan Respublikasının qanunvericiliyinə zidd davranışa səbəb
            olan və yaxud təhrik edən bu kimi başqa fəaliyyətlə məşğul olmaq
            üçün Portaldan istifadə etmək qadağandır. Bu hallarda Operator
            hüquq-mühafizə orqanlarına tam yardım göstərəcək, bu cür fəaliyyətlə
            məşğul olan istifadəçinin şəxsi məlumatlarını səlahiyyətli dövlət
            orqanlarına məlumat vermək öhdəliyi yaradan məhkəmə və inzibati
            aktları yerinə yetirəcək və istənilən vaxt bu cür hesabları silmək
            hüququnu özündə saxlayacaqdır. Portaldan istifadə zamanı portalın və
            ya məzmunun hər hansı proqram təminatı vasitəsilə monitorinqi və ya
            səhifəyə aid müəllif hüquqlarının pozulması yolverilməzdir.
            <br />
            8.2. Operator öz mülahizəsinə görə hesabınızı dayandırmaq və ya ləğv
            etmək, o cümlədən Portaldan istifadə şərtlərinin pozulması halları
            olduqda Portalın bütövlükdə və ya hər hansı hissəsinin cari və ya
            gələcək istifadəsini dayandırmaq hüququna malikdir. Bu dəyişikliklər
            sizin hesabınızın dayanması və ya hesabınıza girişin
            məhdudlaşdırılması, hesabınızdakı bütün məlumatların itirilməsi və
            silinməsi ilə nəticələnə bilər. Agentlik istənilən anda və istənilən
            şəxs tərəfindən Portaldan istifadəni dayandırmaq hüququnu öz
            üzərində saxlayır. Agentlik tərəfindən hesabınız dayandırıldıqda və
            ya ləğv edildikdə buna əsas yaradan hallar aradan qaldırıldıqdan
            sonra hesabınızın bərpası barədə müraciətə baxıla bilər.
            <br />
            <span style={{ fontWeight: "bold" }}>
              {" "}
              9. Məsuliyyətin məhdudlaşdırılması{" "}
            </span>
            <br />
            9.1. İstifadəçilər tərəfindən reyestrə daxil edilmiş məlumatlar
            əsasında sənaye subyektləri arasında yaranmış biznes münasibətlərinə
            görə Agentlik hər hansı ziyan, zərər yaxud əldən çıxmış faydaya görə
            məsuliyyət daşımır. <br />
            <span style={{ fontWeight: "bold" }}> 10. Ödəniş</span>
            <br /> 10.1. Bu portaldan istifadə ödənişsiz qaydada həyata
            keçirilir. Portal daxilində sənaye subyektləri tərəfindən təşkil
            edilən, elan olunan hər hansı haqqlar və s. hallarda ödənişlərin
            həyata keçirilə bilər.
            <br />
            <span style={{ fontWeight: "bold" }}> 11. Tətbiq edilən hüquq</span>
            <br />
            11.1. Bu Qaydalar “Turizm haqqında” Azərbaycan Respublikasının
            qanunu, “Fərdi məlumatlar haqqında” Azərbaycan Respublikasının
            Qanunu, Azərbaycan Respublikası Nazirlər Kabinetinin 23 yanvar
            2020-ci il tarixli 16 nömrəli qərarı ilə təsdiq edilmiş “Turizm
            reyestrinin aparılması qaydası” və digər normativ hüquqi aktlara
            müvafiq olaraq tənzimlənir və təfsir edilir.Bu Qaydalar “Turizm
            haqqında” Azərbaycan Respublikasının qanunu, “Fərdi məlumatlar
            haqqında” Azərbaycan Respublikasının Qanunu, Azərbaycan Respublikası
            Nazirlər Kabinetinin 23 yanvar 2020-ci il tarixli 16 nömrəli qərarı
            ilə təsdiq edilmiş “Turizm reyestrinin aparılması qaydası” və digər
            normativ hüquqi aktlara müvafiq olaraq tənzimlənir və təfsir edilir.{" "}
            <br />
            11.2. Qaydalardan irəli gələn məsələlər və ya mübahisələr müvafiq
            olaraq, ”İnzibati icraat haqqında” Azərbaycan Respublikasının Qanunu
            və məhkəmələr vasitəsi ilə həll edilir.
            {}
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={(e) => handleClose(false)}>
            <FormattedMessage id="key_not_agree" />
          </Button>
          <Button onClick={() => signWithASAN()}>
            <FormattedMessage id="key_asan_sign_confirm" />
          </Button>
        </DialogActions>
      </Dialog>

      {/* Asan signature confirmation modal */}
      <Dialog
        open={asanModalState}
        TransitionComponent={Transition}
        keepMounted
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
      >
        <DialogTitle id="alert-dialog-slide-title">
          <FormattedMessage id="asan_signuture_confirmation" />
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-slide-description"
            component="div"
            className="text-center"
          >
            <div
              style={{
                textAlign: "center",
                fontSize: 14,
                marginBottom: 5,
              }}
            >
              <FormattedMessage id="asan.sign.timer.message" />
            </div>
            <div className="d-flex justify-content-center">
              <div
                style={{
                  textAlign: "center",
                  fontSize: 20,
                }}
              >
                <FormattedMessage id="asan.check.code" />
                {" : "}
                {pin2}
              </div>
            </div>
            <div className="d-flex justify-content-center">
              <div
                className="d-flex justify-content-center align-items-center"
                style={{
                  fontSize: 22,
                  width: 110,
                }}
              >
                <div className="d-flex">
                  <Timer
                    style={{
                      fontSize: 26,
                      marginRight: 5,
                    }}
                  />
                </div>
                <div>
                  {String(Math.floor(seconds / 60)).padStart(2, "0")}
                  {" : "}
                  {String(
                    seconds - Math.floor(seconds / 60) * 60 === 0
                      ? 0
                      : seconds - Math.floor(seconds / 60) * 60
                  ).padStart(2, 0)}
                </div>
              </div>
            </div>
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </Grid>
  );
};

export default Login;

const theme = createMuiTheme({
  palette: {
    // primary: 'purple',
    secondary: {
      // main: "#1A66AC",
      main: "rgb(100, 38, 187)",
    },
    // error: 'red',
  },
});
